import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import TripForm from './TripForm';
import TripWizard from './TripWizard';
import AiImportButton from './components/AiImportButton';
import AiItineraryImportModal from './components/AiItineraryImportModal';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, API_PATH, BASE_PATH } from './config';
import countriesCities from './data/countries_cities.json';
import { deleteSnapshot, getTripsList, saveTripsList } from './utils/offlineStore';
import {
  Plus, Edit2, Archive, Plane, Calendar, Compass, Users,
  DollarSign, AlertCircle, Loader2, ChevronRight
} from 'lucide-react';

// Helper function to find country by city name
const findCountryByCity = (cityName) => {
    if (!cityName) return null;
    
    for (const countryData of countriesCities) {
        if (countryData.cities && countryData.cities.includes(cityName)) {
            return countryData.country;
        }
    }
    return null;
};

const getTripTitle = (trip) => {
    if (!trip.locations) return trip.name;
    
    const locations = trip.locations.split('|');
    const countries = [];
    const seenCountries = new Set();
    
    locations.forEach(location => {
        if (!location) return;
        const parts = location.split(',').map(p => p.trim());
        let country = null;
        
        // Check if the last part is a known country
        const potentialCountry = parts[parts.length - 1];
        const isKnownCountry = countriesCities.some(c => c.country === potentialCountry);
        
        if (isKnownCountry) {
            country = potentialCountry;
        } else {
            // Try finding by city
            country = findCountryByCity(parts[0]);
        }
        
        if (country && !seenCountries.has(country)) {
            seenCountries.add(country);
            countries.push(country);
        }
    });
    
    if (countries.length > 0) {
        return countries.join(", ");
    }
    
    return trip.name;
};

const TripPage = ({ user, setIsAiWorking }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [trips, setTrips] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showForm, setShowForm] = useState(false);
    const [showAiImport, setShowAiImport] = useState(false);
    const [mainTab, setMainTab] = useState(0);
    const [expenseReports, setExpenseReports] = useState([]);
    const [reportsLoading, setReportsLoading] = useState(false);
    const userId = user?.pu_id || null;
    const userParam = userId ? `&user_id=${userId}` : '';

    const fetchTrips = useCallback(async () => {
        const userKey = user?.user_id || user?.id || user?.email || 'default';
        setLoading(true);

        // Instant paint from the cached list (works offline).
        let hadCache = false;
        try {
            const cached = await getTripsList(userKey);
            if (cached) { setTrips(cached); setLoading(false); hadCache = true; }
        } catch (_) {}

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/get.php?t=${Date.now()}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                cache: 'no-cache'
            });

            if (response.ok) {
                const data = await response.json();
                setTrips(data);
                setError('');
                saveTripsList(userKey, data);
            } else if (!hadCache) {
                setError(t('fetch_trips_error'));
            }
        } catch (err) {
            // Offline / network failure: keep the cached list if we have one.
            if (!hadCache) setError(t('fetch_trips_general_error'));
        } finally {
            setLoading(false);
        }
    }, [t, user]);

    useEffect(() => {
        fetchTrips();
    }, [fetchTrips]);

    const fetchExpenseReports = useCallback(async () => {
        if (!userId) return;
        setReportsLoading(true);
        try {
            const res = await fetch(`${API_PATH}/expense_reports.php?action=list${userParam}`);
            const data = await res.json();
            setExpenseReports([...(data.owned || []), ...(data.shared || [])]);
        } finally {
            setReportsLoading(false);
        }
    }, [userId, userParam]);

    useEffect(() => {
        if (mainTab === 1) fetchExpenseReports();
    }, [mainTab, fetchExpenseReports]);

    const handleTripCreated = (tripId, rawResponse) => {
        // Debug: log when parent receives created trip
        // eslint-disable-next-line no-console
        setShowForm(false); // Hide the form
        // Navigate to the created trip page when a tripId is provided
        if (tripId) {
            navigate(`/trip/${tripId}`);
            return;
        }
        // Otherwise refresh the trip list
        fetchTrips(); // Refresh the trip list
    };

    if (loading) {
        return (
            <div className="tw-p-4 sm:tw-p-6">
                <div className="tw-h-8 tw-w-48 tw-rounded-xl tw-bg-surface-3 tw-animate-pulse tw-mb-6" />
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="tw-rounded-2xl tw-bg-surface-2 tw-animate-pulse tw-overflow-hidden">
                            <div className="tw-h-20 tw-bg-surface-3" />
                            <div className="tw-p-4 tw-space-y-3">
                                <div className="tw-h-4 tw-w-3/4 tw-rounded tw-bg-surface-3" />
                                <div className="tw-h-3 tw-w-1/2 tw-rounded tw-bg-surface-3" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="tw-flex tw-justify-center tw-items-center tw-h-80">
                <div className="tw-flex tw-items-center tw-gap-3 tw-bg-red-900/20 tw-border tw-border-red-500/30 tw-rounded-2xl tw-px-6 tw-py-4">
                    <AlertCircle className="tw-text-red-400 tw-shrink-0" size={20} />
                    <span className="tw-text-red-300 tw-text-sm">{error}</span>
                </div>
            </div>
        );
    }

    const handleTripClick = (tripId) => {
        navigate(`/trip/${tripId}`);
    };

    const handleUpdateTrip = (tripId) => {
        navigate(`/trip/${tripId}/edit`);
    };

    const handleArchiveTrip = async (tripId) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE_URL}/trips/archive.php`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ trip_id: tripId })
                });

            if (response.ok) {
                // Drop the trip's offline image; it re-caches on next online open if restored.
                deleteSnapshot(tripId);
                fetchTrips(); // Refresh the trip list
            } else {
                setError(t('archive_trip_error'));
            }
        } catch (err) {
            setError(t('archive_trip_general_error'));
        }
    };

    const calculateDaysUntilStart = (trip) => {
        let tripStartDate = null;
        if (trip.start_date) {
            tripStartDate = new Date(trip.start_date);
        } else if (trip.month_of_year) {
            // Assuming current year for flexible trips if start_date is null
            // This might need refinement if trips can be in future years
            const currentYear = new Date().getFullYear();
            tripStartDate = new Date(currentYear, trip.month_of_year - 1, 1);
        }

        if (tripStartDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0); // Reset time for accurate day calculation
            tripStartDate.setHours(0, 0, 0, 0); // Reset time for accurate day calculation

            const diffTime = tripStartDate.getTime() - today.getTime();
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            return { days: diffDays, label: diffDays === 0 ? t('starts_today') : diffDays > 0 ? t('days_until_start', { days: diffDays }) : t('trip_started') };
        }
        return { days: null, label: "N/A" };
    };

    const getTripStatusChip = (trip) => {
        const { days } = calculateDaysUntilStart(trip);
        if (days === null) return null;
        
        if (days < 0) {
            return <span className="tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-emerald-900/40 tw-text-emerald-400 tw-border tw-border-emerald-500/30">{t('trip_in_progress') || 'In Progress'}</span>;
        } else if (days === 0) {
            return <span className="tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-amber-900/40 tw-text-amber-400 tw-border tw-border-amber-500/30">{t('starts_today')}</span>;
        } else if (days <= 7) {
            return <span className="tw-text-xs tw-font-semibold tw-px-2 tw-py-0.5 tw-rounded-full tw-bg-sky-900/40 tw-text-sky-400 tw-border tw-border-sky-500/30">{t('trip_soon') || 'Soon'}</span>;
        }
        return null;
    };

    return (
        <div className="tw-p-4 sm:tw-p-6 tw-max-w-5xl tw-mx-auto">
            <AiItineraryImportModal
                open={showAiImport}
                onClose={() => setShowAiImport(false)}
                onComplete={(tripId) => {
                    setShowAiImport(false);
                    if (tripId) navigate(`/trip/${tripId}`);
                }}
            />
            {showForm ? (
                <TripWizard onSubmit={handleTripCreated} onCancel={() => setShowForm(false)} setIsAiWorking={setIsAiWorking} />
            ) : (
                <div>
                    {/* ── Tab bar ── */}
                    <div className="tw-flex tw-gap-1 tw-mb-6 tw-bg-surface-2 tw-rounded-2xl tw-p-1 tw-w-fit" role="tablist">
                        <button
                            role="tab"
                            aria-selected={mainTab === 0}
                            onClick={() => setMainTab(0)}
                            className={`tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-transition-all tw-duration-200 ${
                                mainTab === 0
                                    ? 'tw-bg-accent tw-text-white tw-shadow-lg'
                                    : 'tw-text-muted tw-hover:tw-text-muted-fore'
                            }`}
                        >
                            <Plane size={15} aria-hidden="true" />
                            {t('my_trips_title') || 'My Trips'}
                        </button>
                        <button
                            role="tab"
                            aria-selected={mainTab === 1}
                            onClick={() => setMainTab(1)}
                            className={`tw-flex tw-items-center tw-gap-2 tw-px-5 tw-py-2 tw-rounded-xl tw-text-sm tw-font-semibold tw-transition-all tw-duration-200 ${
                                mainTab === 1
                                    ? 'tw-bg-accent tw-text-white tw-shadow-lg'
                                    : 'tw-text-muted tw-hover:tw-text-muted-fore'
                            }`}
                        >
                            <DollarSign size={15} aria-hidden="true" />
                            My Expenses
                        </button>
                    </div>

                    {/* ── My Trips tab ── */}
                    {mainTab === 0 && (
                        <>
                            <div className="tw-flex tw-justify-between tw-items-center tw-mb-6 tw-gap-4 tw-flex-wrap">
                                <h2 className="tw-text-2xl sm:tw-text-3xl tw-font-bold tw-text-muted-fore">
                                    {t('my_trips_title') || 'My Trips'}
                                </h2>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <AiImportButton
                                        variant="pill"
                                        pillText={t('ai_import_trip_btn', 'AI Trip Import')}
                                        label={t('ai_import_trip_label') || 'Create a trip from a pasted itinerary using AI'}
                                        onClick={() => setShowAiImport(true)}
                                    />
                                    <button
                                        onClick={() => setShowForm(true)}
                                        aria-label={t('add_new_trip_btn') || 'Add new trip'}
                                        className="tw-flex tw-items-center tw-gap-2 tw-bg-accent tw-hover:tw-bg-accent-600 tw-text-white tw-font-semibold tw-px-5 tw-py-2.5 tw-rounded-2xl tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.02] tw-shadow-lg tw-shadow-accent/20"
                                    >
                                        <Plus size={18} aria-hidden="true" />
                                        {t('add_new_trip_btn') || 'New Trip'}
                                    </button>
                                </div>
                            </div>

                            {trips.length > 0 ? (
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                                    {trips.map(trip => {
                                        const tripStatus = calculateDaysUntilStart(trip);
                                        const isShared = !!trip.is_shared;
                                        const gradientClass = isShared
                                            ? 'tw-from-emerald-700 tw-to-teal-600'
                                            : 'tw-from-accent-600 tw-to-secondary-dark';

                                        return (
                                            <article
                                                key={trip.trip_id}
                                                className="tw-group tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-overflow-hidden tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.02] tw-hover:tw-shadow-xl tw-hover:tw-shadow-accent/10"
                                            >
                                                {/* Card header gradient */}
                                                <button
                                                    className={`tw-w-full tw-bg-gradient-to-br ${gradientClass} tw-px-4 tw-py-4 tw-flex tw-items-center tw-gap-3 tw-text-left`}
                                                    onClick={() => handleTripClick(trip.trip_id)}
                                                    aria-label={`Open trip: ${getTripTitle(trip)}`}
                                                >
                                                    <span className="tw-p-2 tw-rounded-xl tw-bg-white/20 tw-shrink-0">
                                                        {isShared
                                                            ? <Users size={22} className="tw-text-white" aria-hidden="true" />
                                                            : <Plane size={22} className="tw-text-white" aria-hidden="true" />
                                                        }
                                                    </span>
                                                    <span className="tw-min-w-0 tw-flex-1">
                                                        <span className="tw-block tw-text-base tw-font-bold tw-text-white tw-truncate">
                                                            {getTripTitle(trip)}
                                                        </span>
                                                        {isShared && (
                                                            <span className="tw-text-xs tw-text-white/80">
                                                                {t('shared_by')}: {trip.owner_name || trip.owner_public_id}
                                                            </span>
                                                        )}
                                                    </span>
                                                    <ChevronRight size={18} className="tw-text-white/60 tw-shrink-0 tw-transition-transform tw-duration-200 tw-group-hover:tw-translate-x-1" aria-hidden="true" />
                                                </button>

                                                {/* Card body */}
                                                <div className="tw-px-4 tw-py-3">
                                                    <div className="tw-flex tw-items-center tw-gap-2 tw-text-muted tw-text-sm">
                                                        <Calendar size={14} aria-hidden="true" />
                                                        <span>
                                                            {trip.start_date && trip.end_date
                                                                ? `${trip.start_date} – ${trip.end_date}`
                                                                : `${new Date(0, trip.month_of_year - 1).toLocaleString('default', { month: 'long' })} (${trip.flexible_duration_type})`
                                                            }
                                                        </span>
                                                    </div>
                                                    <div className="tw-flex tw-items-center tw-justify-between tw-mt-2">
                                                        <span className={`tw-text-xs ${tripStatus.days !== null && tripStatus.days <= 7 && tripStatus.days >= 0 ? 'tw-text-amber-400 tw-font-semibold' : 'tw-text-muted'}`}>
                                                            {tripStatus.label}
                                                        </span>
                                                        {getTripStatusChip(trip)}
                                                    </div>
                                                </div>

                                                {/* Card actions */}
                                                <div className="tw-flex tw-justify-end tw-gap-1 tw-px-3 tw-pb-3 tw-pt-1 tw-border-t tw-border-surface-border">
                                                    {(!isShared || trip.shared_permission === 'contributor') && (
                                                        <button
                                                            aria-label={t('update_trip_btn') || 'Edit trip'}
                                                            onClick={(e) => { e.stopPropagation(); handleUpdateTrip(trip.trip_id); }}
                                                            className="tw-p-2 tw-rounded-xl tw-text-accent-glow tw-hover:tw-bg-accent/10 tw-transition-all tw-duration-200"
                                                        >
                                                            <Edit2 size={15} aria-hidden="true" />
                                                        </button>
                                                    )}
                                                    <button
                                                        aria-label={t('archive_trip_btn') || 'Archive trip'}
                                                        onClick={(e) => { e.stopPropagation(); handleArchiveTrip(trip.trip_id); }}
                                                        className="tw-p-2 tw-rounded-xl tw-text-muted tw-hover:tw-bg-amber-500/10 tw-hover:tw-text-amber-400 tw-transition-all tw-duration-200"
                                                    >
                                                        <Archive size={15} aria-hidden="true" />
                                                    </button>
                                                </div>
                                            </article>
                                        );
                                    })}
                                </div>
                            ) : (
                                /* Empty state */
                                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-20 tw-px-4 tw-text-center">
                                    <span className="tw-p-6 tw-rounded-full tw-bg-accent/10 tw-mb-6">
                                        <Compass size={56} className="tw-text-accent" aria-hidden="true" />
                                    </span>
                                    <h3 className="tw-text-xl tw-font-semibold tw-text-muted-fore tw-mb-2">
                                        {t('no_trips_title') || 'No trips yet'}
                                    </h3>
                                    <p className="tw-text-muted tw-text-sm tw-max-w-sm tw-mb-8">
                                        {t('no_trips_description') || 'Start planning your next adventure! Create your first trip to begin.'}
                                    </p>
                                    <button
                                        onClick={() => setShowForm(true)}
                                        className="tw-flex tw-items-center tw-gap-2 tw-bg-accent tw-hover:tw-bg-accent-600 tw-text-white tw-font-semibold tw-px-8 tw-py-3 tw-rounded-2xl tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.02] tw-shadow-xl tw-shadow-accent/25"
                                    >
                                        <Plus size={20} aria-hidden="true" />
                                        {t('create_first_trip') || 'Create Your First Trip'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {/* ── My Expenses tab ── */}
                    {mainTab === 1 && (
                        <div>
                            <div className="tw-flex tw-justify-between tw-items-center tw-mb-6 tw-gap-4 tw-flex-wrap">
                                <h2 className="tw-text-2xl tw-font-bold tw-text-muted-fore">My Expense Reports</h2>
                                <button
                                    onClick={() => navigate('/expenses')}
                                    className="tw-flex tw-items-center tw-gap-2 tw-bg-accent tw-hover:tw-bg-accent-600 tw-text-white tw-font-semibold tw-px-5 tw-py-2.5 tw-rounded-2xl tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.02]"
                                >
                                    <Plus size={18} aria-hidden="true" />
                                    Manage
                                </button>
                            </div>

                            {reportsLoading ? (
                                <div className="tw-flex tw-justify-center tw-items-center tw-h-40">
                                    <Loader2 size={32} className="tw-text-accent tw-animate-spin" aria-label="Loading" />
                                </div>
                            ) : expenseReports.length === 0 ? (
                                <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16 tw-text-center">
                                    <span className="tw-p-5 tw-rounded-full tw-bg-accent/10 tw-mb-4">
                                        <DollarSign size={44} className="tw-text-muted" aria-hidden="true" />
                                    </span>
                                    <p className="tw-text-muted tw-text-sm tw-mb-4">No expense reports yet.</p>
                                    <button
                                        onClick={() => navigate('/expenses')}
                                        className="tw-border tw-border-accent/40 tw-text-accent tw-hover:tw-bg-accent/10 tw-font-semibold tw-px-6 tw-py-2 tw-rounded-xl tw-transition-all tw-duration-200"
                                    >
                                        Create one
                                    </button>
                                </div>
                            ) : (
                                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-4">
                                    {expenseReports.map(r => (
                                        <article
                                            key={r.id}
                                            className="tw-rounded-2xl tw-bg-surface-glass tw-backdrop-blur-md tw-border tw-border-surface-border tw-overflow-hidden tw-cursor-pointer tw-transition-all tw-duration-200 tw-hover:tw-scale-[1.02] tw-hover:tw-shadow-xl tw-hover:tw-shadow-accent/10"
                                            onClick={() => navigate(`/expenses/${r.id}`)}
                                        >
                                            <div className="tw-bg-gradient-to-br tw-from-pink-700 tw-to-rose-600 tw-px-4 tw-py-3 tw-flex tw-items-center tw-gap-2">
                                                <DollarSign size={18} className="tw-text-white" aria-hidden="true" />
                                                <span className="tw-text-white tw-font-bold tw-text-sm tw-truncate tw-flex-1">{r.title}</span>
                                            </div>
                                            <div className="tw-px-4 tw-py-3 tw-space-y-2">
                                                <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-muted tw-text-xs">
                                                    <Plane size={12} aria-hidden="true" />
                                                    <span>{r.trip_name || 'Unassigned'}</span>
                                                </div>
                                                <div className="tw-flex tw-justify-between tw-items-center">
                                                    <span className="tw-text-sm tw-font-semibold tw-text-accent-glow">
                                                        {r.totals && r.totals.length > 0
                                                            ? r.totals.map(t => `${t.currency} ${parseFloat(t.total).toLocaleString()}`).join(' + ')
                                                            : '—'}
                                                    </span>
                                                    <div className="tw-flex -tw-space-x-1" aria-label="Members">
                                                        {(r.members || []).slice(0, 3).map(m => (
                                                            <span
                                                                key={m.pu_id}
                                                                title={m.display_name}
                                                                className="tw-w-6 tw-h-6 tw-rounded-full tw-bg-accent tw-border-2 tw-border-surface-1 tw-flex tw-items-center tw-justify-center tw-text-xs tw-text-white tw-font-bold"
                                                                style={{ backgroundColor: `hsl(${Math.abs(m.display_name.charCodeAt(0) * 37) % 360},55%,40%)` }}
                                                            >
                                                                {m.display_name[0].toUpperCase()}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};


export default TripPage;
